import PropTypes from "prop-types";
import { useState } from "react";
import { Lock, Eye, EyeOff, ChevronDown, ChevronUp } from "lucide-react";
import { formatCurrency } from "../../utils/formatters";
import { cardLogos } from "../../utils/cardLogos";

const CreditCardForm = ({
  method,
  pricing,
  cardNumber,
  setCardNumber,
  cardHolder,
  setCardHolder,
  expiryMonth,
  setExpiryMonth,
  expiryYear,
  setExpiryYear,
  cvv,
  setCvv,
  showCvv,
  setShowCvv,
  address,
  setAddress,
  customerDetails,
  setCustomerDetails,
  saveCard,
  setSaveCard,
  formErrors,
  isProcessing,
  onSubmit,
  error,
}) => {
  const [expiryInput, setExpiryInput] = useState("");
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);

  const supportedCards = method?.supported_cards || method?.features?.supported_cards || [];
  const shouldSaveCard = method?.features?.save_card;

  // Check if customer details are autofilled
  const hasCustomerDetails = customerDetails?.name || customerDetails?.email || customerDetails?.phone;

  // Auto-save card is handled by parent component based on is_recurring/is_preauth
  // Don't show any UI for save_card checkbox



  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\D/g, "");
    const chunks = cleaned.match(/.{1,4}/g);
    return chunks ? chunks.join(" ") : "";
  };

  const getCardBrand = (number) => {
    if (!number) return null;
    const cleaned = number.replace(/\s/g, "");
    
    console.log('Card number cleaned:', cleaned);
    
    if (cleaned.length >= 1) {
      if (/^4/.test(cleaned)) {
        console.log('Detected: Visa');
        return "Visa";
      }
      if (/^5[1-5]/.test(cleaned)) {
        console.log('Detected: Mastercard');
        return "Mastercard";
      }
      if (/^3[47]/.test(cleaned)) {
        console.log('Detected: AMEX');
        return "AMEX";
      }
      if (/^35/.test(cleaned)) {
        console.log('Detected: JCB');
        return "JCB";
      }
    }
    console.log('No brand detected');
    return null;
  };

  const isCardSupported = (brand) => {
    const supported = supportedCards.includes(brand);
    console.log('Brand:', brand, 'Supported:', supported, 'Available cards:', supportedCards);
    return supported;
  };

  const isFormValid = () => {
    const hasCardNumber = cardNumber && cardNumber.replace(/\s/g, "").length >= 13;
    const hasCardHolder = cardHolder && cardHolder.trim().length >= 3;
    const hasExpiry = expiryMonth && expiryYear;
    const hasCvv = cvv && cvv.length >= 3;
    const hasAddress = address.addressLine1 && address.city && address.state && address.postalCode;
    const hasCustomer = customerDetails.name && customerDetails.email && customerDetails.phone;

    const detected = getCardBrand(cardNumber);
    const cardIsSupported = !detected || isCardSupported(detected);
    const cardIsLongEnough = cardNumber.replace(/\s/g, "").length >= 4;

    return hasCardNumber && hasCardHolder && hasExpiry && hasCvv && hasAddress && hasCustomer && cardIsSupported;
  };

  const getCardNumberError = () => {
    const detected = getCardBrand(cardNumber);
    const cardLength = cardNumber.replace(/\s/g, "").length;

    if (detected && !isCardSupported(detected)) {
      return `${detected} cards are not supported`;
    }

    return formErrors.cardNumber;
  };

  const formatExpiry = (value) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length >= 2) {
      return cleaned.slice(0, 2) + "/" + cleaned.slice(2, 4);
    }
    return cleaned;
  };

  const getDisplayExpiry = () => {
    if (!expiryMonth || !expiryYear) return "";
    const month = expiryMonth.padStart(2, "0");
    const year = expiryYear.slice(-2);
    return `${month}/${year}`;
  };

  const detectedBrand = getCardBrand(cardNumber);

  return (
    <div className="px-4 sm:px-6 py-4 sm:py-5">
      {/* Supported Cards - Hidden on mobile since shown in card header */}

      {/* Card Number */}
      <div className="mb-3 sm:mb-4">
        <label className="block text-[10px] sm:text-xs font-medium mb-1.5 sm:mb-2" style={{ color: "#3B3B3B" }}>
          Card Number
        </label>
        <div className="relative">
          <input
            type="text"
            value={formatCardNumber(cardNumber)}
            onChange={(e) => setCardNumber(e.target.value.replace(/\D/g, "").slice(0, 16))}
            placeholder="0000 0000 0000 0000"
            className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 pr-12 border rounded-xl text-xs sm:text-sm focus:outline-none focus:ring-1 ${
              getCardNumberError() ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : "border-gray-300 focus:border-green-500 focus:ring-green-500/20"
            }`}
          />
          {(() => {
            console.log('Render check - detectedBrand:', detectedBrand, 'hasLogo:', !!cardLogos[detectedBrand], 'isSupported:', detectedBrand ? isCardSupported(detectedBrand) : false);
            return detectedBrand && cardLogos[detectedBrand] && isCardSupported(detectedBrand);
          })() && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 w-8 h-5 flex items-center justify-center">
              <img src={cardLogos[detectedBrand]} alt={detectedBrand} className="w-full h-full object-contain" />
            </div>
          )}
        </div>
        {getCardNumberError() && (
          <p className="text-red-500 text-[10px] sm:text-xs mt-1">{getCardNumberError()}</p>
        )}
      </div>

      {/* Cardholder Name */}
      <div className="mb-3 sm:mb-4">
        <label className="block text-[10px] sm:text-xs font-medium mb-1.5 sm:mb-2" style={{ color: "#3B3B3B" }}>
          Cardholder Name
        </label>
        <input
          type="text"
          value={cardHolder}
          onChange={(e) => setCardHolder(e.target.value.toUpperCase())}
          placeholder="JOHN DOE"
          className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-xl text-xs sm:text-sm focus:outline-none focus:ring-1 ${
            formErrors.cardHolder ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : "border-gray-300 focus:border-green-500 focus:ring-green-500/20"
          }`}
        />
        {formErrors.cardHolder && (
          <p className="text-red-500 text-[10px] sm:text-xs mt-1">{formErrors.cardHolder}</p>
        )}
      </div>

      {/* Expiry & CVV */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
        <div>
          <label className="block text-[10px] sm:text-xs font-medium mb-1.5 sm:mb-2" style={{ color: "#3B3B3B" }}>
            Expiry Date
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={expiryInput}
            onChange={(e) => {
              let value = e.target.value.replace(/\D/g, "");

              if (value.length >= 2) {
                value = value.slice(0, 2) + "/" + value.slice(2, 4);
              }

              setExpiryInput(value);

              const cleaned = value.replace(/\D/g, "");
              if (cleaned.length >= 2) {
                setExpiryMonth(cleaned.slice(0, 2));
              } else {
                setExpiryMonth(cleaned);
              }

              if (cleaned.length >= 3) {
                const yearValue = cleaned.slice(2, 4);
                setExpiryYear("20" + yearValue);
              } else {
                setExpiryYear("");
              }
            }}
            placeholder="MM/YY"
            maxLength={5}
            className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-xl text-xs sm:text-sm focus:outline-none focus:ring-1 ${
              formErrors.expiry ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : "border-gray-300 focus:border-green-500 focus:ring-green-500/20"
            }`}
          />
          {formErrors.expiry && <p className="text-red-500 text-[10px] sm:text-xs mt-1">{formErrors.expiry}</p>}
        </div>

        <div>
          <label className="block text-[10px] sm:text-xs font-medium mb-1.5 sm:mb-2" style={{ color: "#3B3B3B" }}>
            CVV
          </label>
          <div className="relative">
            <input
              type={showCvv ? "text" : "password"}
              value={cvv}
              onChange={(e) => setCvv(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="123"
              maxLength={4}
              className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 pr-8 sm:pr-10 border rounded-xl text-xs sm:text-sm focus:outline-none focus:ring-1 ${
                formErrors.cvv ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : "border-gray-300 focus:border-green-500 focus:ring-green-500/20"
              }`}
            />
            <button
              type="button"
              onClick={() => setShowCvv(!showCvv)}
              className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2"
              style={{ color: "#515164" }}
            >
              {showCvv ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {formErrors.cvv && <p className="text-red-500 text-[10px] sm:text-xs mt-1">{formErrors.cvv}</p>}
        </div>
      </div>

      {/* Billing Address */}
      <div className="mb-3 sm:mb-4">
        <h3 className="text-[10px] sm:text-xs font-medium mb-2 sm:mb-3" style={{ color: "#3B3B3B" }}>
          Billing Address
        </h3>
        <div className="space-y-2 sm:space-y-3">
          <input
            type="text"
            value={address.addressLine1}
            onChange={(e) => setAddress({ ...address, addressLine1: e.target.value })}
            placeholder="Street address"
            className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-xl text-xs sm:text-sm focus:outline-none focus:ring-1 ${
              formErrors.addressLine1 ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : "border-gray-300 focus:border-green-500 focus:ring-green-500/20"
            }`}
          />
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <input
              type="text"
              value={address.city}
              onChange={(e) => setAddress({ ...address, city: e.target.value })}
              placeholder="City"
              className={`px-3 sm:px-4 py-2.5 sm:py-3 border rounded-xl text-xs sm:text-sm focus:outline-none focus:ring-1 ${
                formErrors.city ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : "border-gray-300 focus:border-green-500 focus:ring-green-500/20"
              }`}
            />
            <input
              type="text"
              value={address.state}
              onChange={(e) => setAddress({ ...address, state: e.target.value })}
              placeholder="State/Province"
              className={`px-3 sm:px-4 py-2.5 sm:py-3 border rounded-xl text-xs sm:text-sm focus:outline-none focus:ring-1 ${
                formErrors.state ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : "border-gray-300 focus:border-green-500 focus:ring-green-500/20"
              }`}
            />
          </div>
          <input
            type="text"
            value={address.postalCode}
            onChange={(e) => setAddress({ ...address, postalCode: e.target.value.replace(/\D/g, "") })}
            placeholder="Postal code"
            className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-xl text-xs sm:text-sm focus:outline-none focus:ring-1 ${
              formErrors.postalCode ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : "border-gray-300 focus:border-green-500 focus:ring-green-500/20"
            }`}
          />
        </div>
      </div>

      {/* Customer Details */}
      {(hasCustomerDetails || showCustomerDetails) && (
        <div className="mb-3 sm:mb-4">
          <button
            type="button"
            onClick={() => setShowCustomerDetails(!showCustomerDetails)}
            className="w-full flex items-center justify-between text-[10px] sm:text-xs font-medium mb-2 sm:mb-3"
            style={{ color: "#3B3B3B" }}
          >
            <span>Contact Information</span>
            {showCustomerDetails ? (
              <ChevronUp className="w-4 h-4" style={{ color: "#515164" }} />
            ) : (
              <ChevronDown className="w-4 h-4" style={{ color: "#515164" }} />
            )}
          </button>

          {showCustomerDetails && (
            <div className="space-y-2 sm:space-y-3">
              <input
                type="text"
                value={customerDetails.name}
                onChange={(e) => setCustomerDetails({ ...customerDetails, name: e.target.value })}
                placeholder="Full name"
                className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-xl text-xs sm:text-sm focus:outline-none focus:ring-1 ${
                  formErrors.name ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : "border-gray-300 focus:border-green-500 focus:ring-green-500/20"
                }`}
              />
              <input
                type="email"
                value={customerDetails.email}
                onChange={(e) => setCustomerDetails({ ...customerDetails, email: e.target.value })}
                placeholder="Email address"
                className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-xl text-xs sm:text-sm focus:outline-none focus:ring-1 ${
                  formErrors.email ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : "border-gray-300 focus:border-green-500 focus:ring-green-500/20"
                }`}
              />
              <input
                type="tel"
                value={customerDetails.phone}
                onChange={(e) => setCustomerDetails({ ...customerDetails, phone: e.target.value })}
                placeholder="Phone number"
                className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-xl text-xs sm:text-sm focus:outline-none focus:ring-1 ${
                  formErrors.phone ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : "border-gray-300 focus:border-green-500 focus:ring-green-500/20"
                }`}
              />
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {(error || formErrors.submit) && (
        <div className="mb-3 sm:mb-4 p-2.5 sm:p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2">
          <p className="text-[10px] sm:text-xs text-red-700 flex-1">{formErrors.submit || error}</p>
        </div>
      )}

      {/* Pay Button */}
      <button
        onClick={onSubmit}
        disabled={isProcessing || !isFormValid()}
        className="w-full text-white font-semibold py-3 sm:py-3.5 px-6 rounded-xl transition-colors text-xs sm:text-sm flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ backgroundColor: "#01793B" }}
        onMouseEnter={(e) => !isProcessing && isFormValid() && (e.target.style.backgroundColor = "#015c2e")}
        onMouseLeave={(e) => (e.target.style.backgroundColor = "#01793B")}
      >
        {isProcessing ? "Processing..." : "Pay Now"}
      </button>
    </div>
  );
};

CreditCardForm.propTypes = {
  method: PropTypes.object.isRequired,
  pricing: PropTypes.object,
  cardNumber: PropTypes.string.isRequired,
  setCardNumber: PropTypes.func.isRequired,
  cardHolder: PropTypes.string.isRequired,
  setCardHolder: PropTypes.func.isRequired,
  expiryMonth: PropTypes.string.isRequired,
  setExpiryMonth: PropTypes.func.isRequired,
  expiryYear: PropTypes.string.isRequired,
  setExpiryYear: PropTypes.func.isRequired,
  cvv: PropTypes.string.isRequired,
  setCvv: PropTypes.func.isRequired,
  showCvv: PropTypes.bool.isRequired,
  setShowCvv: PropTypes.func.isRequired,
  address: PropTypes.object.isRequired,
  setAddress: PropTypes.func.isRequired,
  customerDetails: PropTypes.object.isRequired,
  setCustomerDetails: PropTypes.func.isRequired,
  formErrors: PropTypes.object.isRequired,
  isProcessing: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  error: PropTypes.string,
};

export default CreditCardForm;
